// sync.js — Stage 4.0 / Phase 9 polling manager.
// Управляет фоновым polling и on-demand refresh данных орг.
//
// Логика:
//   1. Каждые POLL_INTERVAL мс делает GET /api/sync/org/:orgId/version (lightweight)
//   2. Сравнивает полученный updatedAt с lastKnownVersion
//   3. Если изменился — делает полный GET /api/sync/org/:orgId и вызывает onUpdate(freshData)
//   4. Если не изменился — пропускает full fetch
//
// Триггеры дополнительной проверки:
//   - visibilitychange (Alt+Tab возврат к вкладке)
//   - triggerCheck() (вызывается извне — например при switch tab в sidebar)
//   - focusout (после blur — догоняем сразу как юзер закончил ввод, Phase 9.2)
//
// Phase 9.2: pause во время активного ввода в форму.
//   - Если document.activeElement — INPUT/TEXTAREA/SELECT/contenteditable, checkOnce
//     выходит без обновления orgData. Это защищает от race: юзер набивает поле,
//     полл притягивает свежие данные, parent re-render сбивает state модалки.
//   - На focusout (blur) — отложенный triggerCheck('blur') через 600мс. Это:
//     a) Даёт время следующему фокусу приземлиться (Tab между полями не триггерит)
//     b) Если фокус никуда не ушёл — сразу обновляемся, не ждём 30сек.
//
// Использование:
//   const manager = createSyncManager(orgId, currentSyncedAt, onUpdate);
//   manager.start();
//   manager.triggerCheck();  // ручная проверка
//   manager.stop();          // при размонтировании / switch org

import * as api from './api';

const POLL_INTERVAL = 30 * 1000;            // 30 сек — стандартный фоновый polling
const MIN_TRIGGER_INTERVAL = 2 * 1000;      // не чаще 1 раза в 2 сек (от visibilitychange spam)
const BLUR_DEBOUNCE_MS = 600;               // задержка после blur перед triggerCheck (даём фокусу приземлиться)

// Phase 9.2: проверка что юзер сейчас активно вводит в форму.
// Если ИСТИНА — checkOnce пропустит цикл, чтобы не сбить state модалки re-render-ом.
function isUserActivelyEditing() {
  try {
    if (typeof document === 'undefined') return false;
    const ae = document.activeElement;
    if (!ae) return false;
    const tag = ae.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (ae.isContentEditable) return true;
    return false;
  } catch (e) {
    return false; // на ошибке детектора — не блокируем polling
  }
}

// onCheck(reason) — Phase 9.3: вызывается после каждого successful GET /version,
// независимо от того менялись ли данные. Это даёт UI точную точку «когда последний
// раз я знал что сервер живой», чтобы показывать «Обновлено N сек назад».
export function createSyncManager({ orgId, initialSyncedAt, onUpdate, onError, onCheck }) {
  let lastKnownVersion = initialSyncedAt ? String(initialSyncedAt) : null;
  let intervalId = null;
  let lastTriggerAt = 0;
  let stopped = false;
  let visibilityHandler = null;
  let blurHandler = null;
  let blurTimer = null;

  // Сравнить версию с сервером, при изменении — затянуть свежие данные
  async function checkOnce(reason = 'poll') {
    if (stopped) return;
    // Phase 9.2: пауза во время активного ввода в форму. Применимо ко всем reasons
    // (poll/visibility/manual/blur) — лучше пропустить цикл, чем сбить state формы.
    // Следующий цикл polling или blur-trigger догонят свежие данные.
    if (isUserActivelyEditing()) {
      // Лог только для poll, чтобы не спамить — visibility/manual видно по trigger-логам
      if (reason === 'poll') {
        try { console.log('[POLL] skipped (user editing)'); } catch {}
      }
      return;
    }
    try {
      const ver = await api.getOrgVersion(orgId);
      if (!ver || !ver.updatedAt) return; // server недоступен или 401 — игнорим
      // Phase 9.3: фиксируем что сервер ответил — UI обновит «Обновлено N сек назад»
      if (typeof onCheck === 'function') onCheck({ reason });
      const remote = String(ver.updatedAt);
      if (lastKnownVersion && remote === lastKnownVersion) {
        // No change — skip full fetch
        return;
      }
      // Version changed — fetch full org data
      const fresh = await api.loadOrgFromServer(orgId);
      if (!fresh || !fresh.name) return;
      // Save new version anchor (берём из ответа full fetch — он точнее, чем version-only)
      const newVersion = fresh._syncedAt ? String(fresh._syncedAt) : remote;
      lastKnownVersion = newVersion;
      if (typeof onUpdate === 'function') onUpdate(fresh, { reason });
    } catch (err) {
      if (typeof onError === 'function') onError(err, { reason });
    }
  }

  // Внешний trigger (Alt+Tab возврат, switch sidebar tab, после закрытия формы)
  function triggerCheck(reason = 'manual') {
    const now = Date.now();
    if (now - lastTriggerAt < MIN_TRIGGER_INTERVAL) return; // throttle
    lastTriggerAt = now;
    checkOnce(reason);
  }

  function handleVisibility() {
    // Когда вкладка снова видима — сразу проверяем (вместо ожидания следующего polling-цикла)
    if (!document.hidden) triggerCheck('visibility');
  }

  // Phase 9.2: после blur INPUT/TEXTAREA — догоняем сразу, не ждём 30 сек polling-цикла.
  // Откладываем на BLUR_DEBOUNCE_MS чтобы Tab между полями (input1 → input2) не триггерил —
  // следующий focus встанет до таймера и isUserActivelyEditing вернёт true → checkOnce пропустит.
  function handleBlur() {
    if (blurTimer) clearTimeout(blurTimer);
    blurTimer = setTimeout(() => {
      blurTimer = null;
      if (!stopped && !isUserActivelyEditing()) {
        triggerCheck('blur');
      }
    }, BLUR_DEBOUNCE_MS);
  }

  function start() {
    if (intervalId) return; // уже запущен
    stopped = false;
    intervalId = setInterval(() => checkOnce('poll'), POLL_INTERVAL);
    visibilityHandler = handleVisibility;
    blurHandler = handleBlur;
    document.addEventListener('visibilitychange', visibilityHandler);
    document.addEventListener('focusout', blurHandler);
  }

  function stop() {
    stopped = true;
    if (intervalId) { clearInterval(intervalId); intervalId = null; }
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
    if (blurHandler) {
      document.removeEventListener('focusout', blurHandler);
      blurHandler = null;
    }
  }

  // Обновляет анкер вручную — например после успешного PUT /api/sync/org,
  // чтобы следующий polling не триггерил лишний refetch.
  function setVersion(newSyncedAt) {
    if (newSyncedAt) lastKnownVersion = String(newSyncedAt);
  }

  // Phase 10: возвращает текущую известную версию для optimistic locking при PUT.
  // Manager обновляет её после каждого successful poll-fetch и после каждого
  // успешного PUT (через setVersion из App.js callback). Это надёжнее чем брать
  // из orgData._syncedAt — там значение «зашитое» при load и не обновляется автоматически.
  function getVersion() {
    return lastKnownVersion;
  }

  return { start, stop, triggerCheck, setVersion, getVersion };
}
